import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { camelizeKeys } from "lib/helpers";
import { countlessPagingParam } from "lib/helpers/pagingItemCount";
import { BATTERY_UPSELL_PARAMS } from "lib/misc/leadSegments";
import { providerLeadsApi } from "rdx/modules/projects/endpoints";
import { PROJECT_TAGS } from "rdx/modules/projects/tags";
import { LEAD_GROUPS_TAGS } from "rdx/modules/leadGroups/tags";
import { api } from "rdx/services/api";
import { LEADS_TAGS } from "./tags";
import type {
  AbandonLeadResultT,
  GetLeadsTableParamsT,
  LeadsTableApiResponseT,
  LeadsTableResponseT,
  ReopenAbandonedLeadResultT,
} from "./tableTypes";
import { AbandonLeadResultSchema, LeadsTableResponseSchema, ReopenAbandonedLeadResultSchema } from "./tableSchemas";

const LEAD_CAMPAIGN_ACTIVITY_TAG = "LeadCampaignActivity" as const;

const NEW_LEADS_KPI_CODE = "leads";
const LEAD_IMPORT_UPLOAD_MODE = "lead_import";

export type LeadImportSkippedRowT = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  reason: string;
};

export type LeadImportResultT = {
  importedCount: number;
  skippedCount: number;
  skippedRows: LeadImportSkippedRowT[];
  leadGroup?: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
};

export type ImportLeadsArgsT = {
  file: File;
  leadGroupName?: string;
};

type LeadImportResponseT = {
  body: {
    imported_count: number;
    skipped_count: number;
    skipped_rows: Array<{
      first_name: string | null;
      last_name: string | null;
      email: string | null;
      phone_number: string | null;
      street_address: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
      reason: string;
    }>;
    lead_group?: {
      id: number;
      name: string;
      created_at: string;
      updated_at: string;
    };
  };
};

type LeadImportUploadConfigT = {
  key: string;
  signed_url: string;
  headers?: Record<string, string>;
};

const uploadError = (): FetchBaseQueryError => ({
  status: "CUSTOM_ERROR",
  error: "We couldn't upload this CSV. Please try again.",
});

export const leadsApi = api
  .enhanceEndpoints({
    addTagTypes: [
      LEADS_TAGS.LEADS_TABLE,
      PROJECT_TAGS.PRE_SALES_KPIS,
      LEAD_GROUPS_TAGS.LEAD_GROUPS,
      LEAD_CAMPAIGN_ACTIVITY_TAG,
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      importLeads: builder.mutation<LeadImportResultT, ImportLeadsArgsT>({
        queryFn: async ({ file, leadGroupName }, { signal }, _extraOptions, baseQuery) => {
          const configResult = await baseQuery({
            url: "/uploader_config",
            params: { mode: LEAD_IMPORT_UPLOAD_MODE, filename: file.name },
          });
          if (configResult.error) {
            return { error: configResult.error };
          }

          const config = configResult.data as LeadImportUploadConfigT;
          if (!config?.key || !config?.signed_url) {
            return { error: uploadError() };
          }

          try {
            const uploadResponse = await fetch(config.signed_url, {
              method: "PUT",
              headers: config.headers,
              body: file,
              signal,
            });
            if (!uploadResponse.ok) {
              return { error: uploadError() };
            }
          } catch {
            return { error: uploadError() };
          }

          const importResult = await baseQuery({
            url: "/leads/import",
            method: "POST",
            body: { key: config.key, ...(leadGroupName?.trim() && { lead_group_name: leadGroupName.trim() }) },
          });
          if (importResult.error) {
            return { error: importResult.error };
          }

          const response = importResult.data as LeadImportResponseT;
          return { data: camelizeKeys(response.body) as LeadImportResultT };
        },
        invalidatesTags: (result) => [
          ...(result?.importedCount ? [LEADS_TAGS.LEADS_TABLE] : []),
          ...(result?.leadGroup ? [LEAD_GROUPS_TAGS.LEAD_GROUPS] : []),
        ],
        onQueryStarted: (_args, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              if (!data.importedCount) {
                return;
              }

              dispatch(
                providerLeadsApi.util.updateQueryData("getPreSalesKpis", undefined, (cachedData) => {
                  const newLeadsKpi = cachedData.kpis.find((kpi) => kpi.code === NEW_LEADS_KPI_CODE);
                  if (newLeadsKpi) {
                    newLeadsKpi.value += data.importedCount;
                  } else {
                    cachedData.kpis.push({ code: NEW_LEADS_KPI_CODE, value: data.importedCount });
                  }
                }),
              );
            })
            .catch(() => undefined);
        },
      }),
      getLeadsTable: builder.query<LeadsTableResponseT, GetLeadsTableParamsT>({
        query: ({ page, filters = {} }) => ({
          url: "projects_pre_sales",
          params: {
            ...filters,
            p: page,
            limit: 20,
            status: filters.status ?? "active",
            sort: filters.sort ?? "coalesced_updated_at_desc",
            ...BATTERY_UPSELL_PARAMS,
            ...countlessPagingParam(filters, { defaults: { status: "active" } }),
          },
        }),
        transformResponse: (response: LeadsTableApiResponseT) =>
          LeadsTableResponseSchema.parse(camelizeKeys(response.body)),
        providesTags: [LEADS_TAGS.LEADS_TABLE],
      }),
      abandonLead: builder.mutation<AbandonLeadResultT, { leadId: number }>({
        query: ({ leadId }) => ({
          url: `leads/${leadId}/abandon`,
          method: "POST",
        }),
        transformResponse: (response: LeadsTableApiResponseT) =>
          AbandonLeadResultSchema.parse(camelizeKeys(response.body)),
        invalidatesTags: (_result, _error, { leadId }) => [
          LEADS_TAGS.LEADS_TABLE,
          { type: LEAD_CAMPAIGN_ACTIVITY_TAG, id: leadId },
        ],
      }),
      reopenAbandonedLead: builder.mutation<ReopenAbandonedLeadResultT, { leadId: number }>({
        query: ({ leadId }) => ({
          url: `leads/${leadId}/abandon`,
          method: "DELETE",
        }),
        transformResponse: (response: LeadsTableApiResponseT) =>
          ReopenAbandonedLeadResultSchema.parse(camelizeKeys(response.body)),
        invalidatesTags: (_result, _error, { leadId }) => [
          LEADS_TAGS.LEADS_TABLE,
          { type: LEAD_CAMPAIGN_ACTIVITY_TAG, id: leadId },
        ],
      }),
    }),
  });

export const { useGetLeadsTableQuery, useImportLeadsMutation, useAbandonLeadMutation, useReopenAbandonedLeadMutation } =
  leadsApi;
